import { Cached, Delete, Edit, MenuOpen, Newspaper } from '@mui/icons-material'
import { Box, Button, Chip, Divider, Grid, IconButton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_TableOptions,
} from 'material-react-table'
import { useConfirm } from 'material-ui-confirm'
import { FunctionComponent, useMemo, useState } from 'react'

import AuditIconButton from '../../../../base/components/Auditoria/AuditIconButton'
import { SimpleContainerBox } from '../../../../base/components/Container/SimpleBox'
import SimpleMenu, { SimpleMenuItem } from '../../../../base/components/MyMenu/SimpleMenu'
import StackMenu from '../../../../base/components/MyMenu/StackMenu'
import { StackMenuItem } from '../../../../base/components/MyMenu/StackMenuItem'
import Breadcrumb from '../../../../base/components/Template/Breadcrumb/Breadcrumb'
import { PAGE_DEFAULT, PageInputProps } from '../../../../interfaces'
import { genApiQuery, genReplaceEmpty } from '../../../../utils/helper'
import { MuiTableAdvancedOptionsProps } from '../../../../utils/muiTable/muiTableAdvancedOptionsProps'
import { notDanger, notSuccess } from '../../../../utils/notification'
import { swalClose, swalException, swalLoading } from '../../../../utils/swal'
import { apiTipoArticuloEliminar } from '../../api/apiTipoArticuloEliminar'
import { apiTipoArticuloListado } from '../../api/apiTipoArticuloListado'
import { tipoArticuloRoutesMap } from '../../tipoArticuloRoutes'
import TipoArticuloActualizarDialog from '../actualizar/TipoArticuloActualizarDialog'
import TipoArticuloRegistroDialog from '../registro/TipoArticuloRegistroDialog'
import { TipoArticuloProps } from '../../../../interfaces/tipoArticulo.ts'

interface OwnProps {}

type Props = OwnProps

const tableColumns: MRT_ColumnDef<TipoArticuloProps>[] = [
  {
    accessorKey: 'codigo',
    header: 'Código',
    size: 80,
  },
  {
    accessorKey: 'descripcion',
    header: 'Descripción',
    enableSorting: false,
    size: 150,
  },
  {
    accessorKey: 'notas',
    header: 'Notas Rápidas',
    enableColumnFilter: false,
    Cell: ({ cell }) => {
      return genReplaceEmpty(cell.getValue<string[]>(), []).map(
        (item: string, index: number) => {
          return <Chip key={index} label={item} size="small" sx={{ mr: 0.5 }} />
        },
      )
    },
  },
]

const TipoArticuloListado: FunctionComponent<Props> = (props) => {
  const [openNuevoTipoArticulo, setOpenNuevoTipoArticulo] = useState<boolean>(false)
  const [selectedTipoArticulo, setSelectedTipoArticulo] =
    useState<TipoArticuloProps | null>(null)
  const [openActualizarTipoArticulo, setOpenActualizarTipoArticulo] =
    useState<boolean>(false)
  // ESTADO DATATABLE
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: PAGE_DEFAULT.page,
    pageSize: PAGE_DEFAULT.limit,
  })
  const [rowCount, setRowCount] = useState(0)
  const [isRefetching, setIsRefetching] = useState(false)
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
  // FIN ESTADO DATATABLE
  const handleRefreshTable = async () => {
    setIsRefetching(true) // Establece el estado de "isRefetching" como verdadero para mostrar el indicador de carga

    try {
      await refetch() // Realiza la recarga de los datos utilizando la función "refetch" proporcionada por react-query
    } catch (error) {
      console.error('Error al recargar los datos:', error)
    }

    setIsRefetching(false) // Establece el estado de "isRefetching" como falso para ocultar el indicador de carga
  }
  const { data, isError, isLoading, status, refetch } = useQuery({
    queryKey: [
      'tipoArticuloListado',
      columnFilters,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
    ],
    queryFn: async () => {
      const query = genApiQuery(columnFilters)
      const fetchPagination: PageInputProps = {
        ...PAGE_DEFAULT,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        reverse: sorting.length <= 0,
        query,
      }
      const { pageInfo, docs } = await apiTipoArticuloListado(fetchPagination)
      setRowCount(pageInfo.totalDocs)
      return docs
    },
    refetchInterval: false,
    refetchOnWindowFocus: false,
  })
  const columns = useMemo(() => tableColumns, [])
  const confirm = useConfirm()

  /**
   * @description Eliminación de un tipo de articulo
   * @param row
   */
  const handleDeleteRow = async (row: TipoArticuloProps) => {
    const { confirmed } = await confirm({
      title: 'Confirmar Eliminación',
      content: `¿Estás seguro de que deseas eliminar el registro "${row.codigo} - ${row.descripcion}"?`,
    })
    if (!confirmed) return
    swalLoading()
    try {
      const resp = await apiTipoArticuloEliminar(row.codigo)
      if (resp) {
        notSuccess('Registro eliminado correctamente')
        await refetch() // Refresh the table data after deletion
      } else {
        notDanger('Eliminación cancelada o no se pudo completar.')
      }
    } catch (error) {
      swalException(error)
      notDanger('Error al eliminar el upsert.')
    } finally {
      swalClose()
    }
  }

  return (
    <>
      <SimpleContainerBox maxWidth={'lg'}>
        <Breadcrumb routeSegments={[tipoArticuloRoutesMap.gestion]} />
        <StackMenu>
          <StackMenuItem>
            <Button
              size={'small'}
              variant="contained"
              onClick={() => setOpenNuevoTipoArticulo(true)}
              startIcon={<Newspaper />}
              color={'primary'}
            >
              Nuevo Tipo Articulo
            </Button>
          </StackMenuItem>
        </StackMenu>
        <Grid container>
          <Grid size={12}>
            <MaterialReactTable
              {...(MuiTableAdvancedOptionsProps as MRT_TableOptions<TipoArticuloProps>)}
              columns={columns}
              data={data ?? []}
              initialState={{
                showColumnFilters: true,
                columnVisibility: {
                  descripcion: true,
                },
              }}
              enableRowActions={true}
              manualFiltering
              manualPagination
              manualSorting
              muiToolbarAlertBannerProps={
                isError ? { color: 'error', children: 'Error loading data' } : undefined
              }
              onColumnFiltersChange={setColumnFilters}
              onPaginationChange={setPagination}
              onSortingChange={setSorting}
              enableDensityToggle={false}
              enableGlobalFilter={false}
              rowCount={rowCount}
              state={{
                isLoading,
                columnFilters,
                pagination,
                showAlertBanner: isError,
                showProgressBars: isRefetching,
                density: 'compact',
                sorting,
                rowSelection,
              }}
              muiSearchTextFieldProps={{
                variant: 'outlined',
                placeholder: 'Busqueda',
                InputLabelProps: { shrink: true },
                size: 'small',
              }}
              positionActionsColumn={'first'}
              renderRowActions={({ row }) => (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: '0.5rem',
                    width: 100,
                  }}
                >
                  <SimpleMenu
                    menuButton={
                      <>
                        <IconButton aria-label="menuGestionProductos">
                          <MenuOpen />
                        </IconButton>
                      </>
                    }
                  >
                    <SimpleMenuItem
                      onClick={() => {
                        setOpenActualizarTipoArticulo(true)
                        setSelectedTipoArticulo(row.original)
                      }}
                    >
                      <Edit />
                      Editar
                    </SimpleMenuItem>
                    <Divider />
                    <SimpleMenuItem
                      onClick={() => handleDeleteRow(row.original)}
                      sx={{ color: 'error.main' }}
                    >
                      <Delete />
                      Eliminar
                    </SimpleMenuItem>
                  </SimpleMenu>
                  <AuditIconButton row={row.original} />
                </div>
              )}
              //   enableRowSelection
              //   onRowSelectionChange={setRowSelection}
              renderTopToolbarCustomActions={({ table }) => {
                return (
                  <Box
                    sx={{ display: 'flex', gap: '1rem', p: '0.5rem', flexWrap: 'wrap' }}
                  >
                    <IconButton onClick={handleRefreshTable} disabled={isRefetching}>
                      <Cached />
                    </IconButton>
                  </Box>
                )
              }}
              muiTableProps={{
                sx: {
                  tableLayout: 'fixed',
                },
              }}
              displayColumnDefOptions={{
                'mrt-row-actions': {
                  muiTableHeadCellProps: {
                    align: 'center',
                  },
                  size: 35,
                },
              }}
            />
            <TipoArticuloRegistroDialog
              id="tipoArticuloRegistroDialog"
              keepMounted={false}
              open={openNuevoTipoArticulo}
              onClose={(value?: TipoArticuloProps) => {
                if (value) {
                  refetch().then()
                }
                setOpenNuevoTipoArticulo(false)
              }}
            />
            <TipoArticuloActualizarDialog
              id="tipoArticuloActualizarDialog"
              keepMounted={false}
              open={openActualizarTipoArticulo}
              //@ts-ignore
              tipoArticulo={selectedTipoArticulo}
              onClose={(resp?: TipoArticuloProps) => {
                setOpenActualizarTipoArticulo(false)
                if (resp) {
                  refetch().then()
                }
              }}
            />
          </Grid>
        </Grid>
      </SimpleContainerBox>
    </>
  )
}

export default TipoArticuloListado
